import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../firebase/AuthContext";
import { getApiUrl } from "../../../config/api";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import {
  t,
  bi,
  formatDate,
  Section,
  AllNews,
  StatPill,
  ArticlePreviewProvider,
} from "./shared/trendReportShared";

export default function TrendReportDetailPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { date } = useParams();

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };
  const [language, setLanguage] = useState(getLangFromUrl());
  useEffect(() => {
    setLanguage(getLangFromUrl());
    // eslint-disable-next-line
  }, [location.search]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      // Keep location.state: it carries the list page / range to go back to.
      navigate(
        { search: params.toString() },
        { replace: true, state: location.state },
      );
    }
    // eslint-disable-next-line
  }, [language]);

  const backToList = () => {
    // Back to the page and date range the report was opened from (set by the
    // feed's onOpen); a direct link or email opens page 1. lang follows this
    // page in case it was switched here.
    const params = new URLSearchParams(location.state?.listSearch || "");
    params.set("lang", language);
    navigate({ pathname: "/trend-reports", search: `?${params.toString()}` });
  };

  const [state, setState] = useState({ status: "loading", report: null });

  useEffect(() => {
    if (!currentUser || !date) return;
    let cancelled = false;
    (async () => {
      setState({ status: "loading", report: null });
      try {
        const token = await currentUser.getIdToken();
        const res = await axios.get(
          `${getApiUrl()}/api/v2/trend-reports/${date}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setState({ status: "ok", report: res.data?.report || null });
      } catch (e) {
        if (!cancelled) {
          setState({
            status: e?.response?.status === 404 ? "not_found" : "error",
            report: null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, date]);

  const { report } = state;
  const payload = report?.payload || {};
  const stats = payload.stats || {};
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const title = bi(payload.title, language) || t("fallbackTitle", language);

  return (
    <div className="w-full h-screen overflow-y-auto flex bg-slate-100 dark:bg-dashboard-bg">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />
      <div className="flex-1 overflow-y-auto p-6 pt-16">
        <div className="w-[95%] md:w-3/4 mx-auto">
          <button
            type="button"
            onClick={backToList}
            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 mb-4"
          >
            ‹ {t("backToReports", language)}
          </button>

          {state.status === "loading" && (
            <p className="text-sm text-gray-400">{t("loading", language)}</p>
          )}
          {state.status === "error" && (
            <p className="text-sm text-red-500">{t("error", language)}</p>
          )}
          {state.status === "not_found" && (
            <p className="text-sm text-gray-400">
              {t("reportNotFound", language)}
            </p>
          )}
          {state.status === "ok" && !report && (
            <p className="text-sm text-gray-400">{t("empty", language)}</p>
          )}

          {state.status === "ok" && report && (
            <ArticlePreviewProvider>
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="p-5 border-b border-gray-100 dark:border-gray-700/60">
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">
                    {title}
                  </h1>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {formatDate(
                        payload.report_date || report.report_date,
                        language,
                      )}
                    </span>
                    <span
                      className="text-gray-300 dark:text-gray-600"
                      aria-hidden
                    >
                      ·
                    </span>
                    <span className="text-xs text-gray-400">
                      {t("subtitle", language)}
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  <div className="flex items-center gap-6">
                    <StatPill
                      value={stats.total_items ?? 0}
                      label={t("items", language)}
                    />
                    <StatPill
                      value={stats.applications_with_news ?? stats.topics_with_news ?? 0}
                      label={t("applicationsWithNews", language)}
                    />
                  </div>

                  {sections.map((section, i) => (
                    <Section
                      key={i}
                      section={section}
                      lang={language}
                      sources={payload.sources}
                    />
                  ))}

                  <AllNews
                    groups={report.news}
                    lang={language}
                    sources={payload.sources}
                  />
                </div>
              </div>
            </ArticlePreviewProvider>
          )}
        </div>
      </div>
    </div>
  );
}
